import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import { Box, Card, Container, Grid, Tab, Tabs } from '@mui/material';
import { TabsContainerWrapper } from 'src/layouts/TabsContainerWrapper';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import routes from 'src/Resources/routes.json';
import InstallationSearch from './InstallationSearch';
import { FormattedMessage } from 'react-intl';
import { UserContext } from '../../../contexts/userContext';
import { InstallationsContext } from '../../../contexts/InstallationsContextProvider';
import ListIcon from '@mui/icons-material/List';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TreeView from '../Tree/treeView';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';
import { UserType } from '../../../interfaces/UserTypes';
import { PARTNER_TICKETS_ENABLED } from '../../../config/featureFlags';
import SalidomoInstallation from './Installation';

interface InstallationTabsProps {
  product: number;
}

function SalidomoInstallationTabs(props: InstallationTabsProps) {
  const location = useLocation();
  const context = useContext(UserContext);
  const { currentUser } = context;
  const tabList = [
    'batteryview',
    'information',
    // 'manage',
    'overview',
    'log',
    'history',
    'installationTickets',
    'documents'
  ];

  const [currentTab, setCurrentTab] = useState<string>(undefined);

  const {
    salidomoInstallations,
    fetchAllInstallations,
    socket,
    openSocket,
    closeSocket
  } = useContext(InstallationsContext);
  const { product, setProduct } = useContext(ProductIdContext);

  //The following useEffect is for the current tab to be bold. Based on the location, we set the corresponding tab to be bold
  useEffect(() => {
    let path = location.pathname.split('/');

    if (path[path.length - 2] === 'list') {
      setCurrentTab('list');
      fetchAllInstallations(product, false);
    } else if (path[path.length - 2] === 'tree') {
      setCurrentTab('tree');
    } else {
      //Even if we are located at path: /batteryview/mainstats, we want the BatteryViewSalidomo tab to be bold
      setCurrentTab(path.find((pathElement) => tabList.includes(pathElement)));
    }
  }, [location]);

  useEffect(() => {
    setProduct(props.product);
  }, [props.product]);

  useEffect(() => {
    if (product == props.product) {
      fetchAllInstallations(product);
    }
  }, [product]);

  const handleTabsChange = (_event: ChangeEvent<{}>, value: string): void => {
    setCurrentTab(value);
  };

  //Manually compute the path when the user clicks a tab
  const navigateToTabPath = (pathname: string, tab_value: string): string => {
    let pathlist = pathname.split('/');
    let ret_path = '';
    for (let i = 1; i < pathlist.length; i++) {
      if (Number.isNaN(Number(pathlist[i]))) {
        ret_path += '/';
        ret_path += pathlist[i];
      } else {
        ret_path += '/';
        ret_path += pathlist[i];
        ret_path += '/';
        break;
      }
    }

    ret_path += tab_value;
    return ret_path;
  };

  const singleInstallationTabs =
    currentUser.userType == UserType.admin
      ? [
          {
            value: 'batteryview',
            label: (
              <FormattedMessage
                id="batteryview"
                defaultMessage="Battery View"
              />
            )
          },
          {
            value: 'overview',
            label: <FormattedMessage id="overview" defaultMessage="Overview" />
          },
          {
            value: 'log',
            label: <FormattedMessage id="log" defaultMessage="Log" />
          },

          // {
          //   value: 'manage',
          //   label: (
          //     <FormattedMessage
          //       id="manage"
          //       defaultMessage="Access Management"
          //     />
          //   )
          // },

          {
            value: 'information',
            label: (
              <FormattedMessage id="information" defaultMessage="Information" />
            )
          },
          {
            value: 'history',
            label: (
              <FormattedMessage
                id="history"
                defaultMessage="History Of Actions"
              />
            )
          },
          {
            value: 'installationTickets',
            label: <FormattedMessage id="tickets" defaultMessage="Tickets" />
          },
          {
            value: 'documents',
            label: <FormattedMessage id="documentsTab" defaultMessage="Documents" />
          }
        ]
      : [
          {
            value: 'batteryview',
            label: (
              <FormattedMessage
                id="batteryview"
                defaultMessage="Battery View"
              />
            )
          },
          {
            value: 'overview',
            label: <FormattedMessage id="overview" defaultMessage="Overview" />
          },

          {
            value: 'information',
            label: (
              <FormattedMessage id="information" defaultMessage="Information" />
            )
          },
          ...(PARTNER_TICKETS_ENABLED
            ? [
                {
                  value: 'installationTickets',
                  label: (
                    <FormattedMessage id="tickets" defaultMessage="Tickets" />
                  )
                }
              ]
            : []),
          {
            value: 'documents',
            label: <FormattedMessage id="documentsTab" defaultMessage="Documents" />
          }
        ];

  const tabs =
    currentTab != 'list' &&
    currentTab != 'tree' &&
    !location.pathname.includes('folder') &&
    currentUser.userType == UserType.admin
      ? [
          {
            value: 'list',
            icon: <ListIcon id="mode-toggle-button-list-icon" />
          },
          {
            value: 'tree',
            icon: <AccountTreeIcon id="mode-toggle-button-tree-icon" />
          },
          {
            value: 'batteryview',
            label: (
              <FormattedMessage
                id="batteryview"
                defaultMessage="Battery View"
              />
            )
          },
          {
            value: 'overview',
            label: <FormattedMessage id="overview" defaultMessage="Overview" />
          },
          {
            value: 'log',
            label: <FormattedMessage id="log" defaultMessage="Log" />
          },

          // {
          //   value: 'manage',
          //   label: (
          //     <FormattedMessage
          //       id="manage"
          //       defaultMessage="Access Management"
          //     />
          //   )
          // },

          {
            value: 'information',
            label: (
              <FormattedMessage id="information" defaultMessage="Information" />
            )
          },
          {
            value: 'history',
            label: (
              <FormattedMessage
                id="history"
                defaultMessage="History Of Actions"
              />
            )
          },
          {
            value: 'installationTickets',
            label: <FormattedMessage id="tickets" defaultMessage="Tickets" />
          },
          {
            value: 'documents',
            label: <FormattedMessage id="documentsTab" defaultMessage="Documents" />
          }
        ]
      : currentTab != 'list' &&
        currentTab != 'tree' &&
        !location.pathname.includes('folder') &&
        currentUser.userType == UserType.client
      ? [
          {
            value: 'list',
            icon: <ListIcon id="mode-toggle-button-list-icon" />
          },
          {
            value: 'tree',
            icon: <AccountTreeIcon id="mode-toggle-button-tree-icon" />
          },
          {
            value: 'batteryview',
            label: (
              <FormattedMessage
                id="batteryview"
                defaultMessage="Battery View"
              />
            )
          },
          {
            value: 'overview',
            label: <FormattedMessage id="overview" defaultMessage="Overview" />
          },

          {
            value: 'information',
            label: (
              <FormattedMessage id="information" defaultMessage="Information" />
            )
          },
          ...(PARTNER_TICKETS_ENABLED
            ? [
                {
                  value: 'installationTickets',
                  label: (
                    <FormattedMessage id="tickets" defaultMessage="Tickets" />
                  )
                }
              ]
            : []),
          {
            value: 'documents',
            label: <FormattedMessage id="documentsTab" defaultMessage="Documents" />
          }
        ]
      : [
          {
            value: 'list',
            icon: <ListIcon id="mode-toggle-button-list-icon" />
          },
          {
            value: 'tree',
            icon: <AccountTreeIcon id="mode-toggle-button-tree-icon" />
          }
        ];

  return salidomoInstallations.length > 1 ? (
    <>
      <Container maxWidth="xl" sx={{ marginTop: '20px' }} className="mainframe">
        <TabsContainerWrapper>
          <Tabs
            onChange={handleTabsChange}
            value={currentTab}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            textColor="primary"
            indicatorColor="primary"
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                icon={tab.icon}
                component={Link}
                label={tab.label}
                to={
                  tab.value === 'list' || tab.value === 'tree'
                    ? routes[tab.value]
                    : navigateToTabPath(location.pathname, routes[tab.value])
                }
                id={`tour-tab-${tab.value}`}
              />
            ))}
          </Tabs>
        </TabsContainerWrapper>
        <Card variant="outlined">
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="stretch"
            spacing={0}
          >
            <Routes>
              <Route
                path={routes.list + '*'}
                element={
                  <Grid item xs={12}>
                    <Box p={4}>
                      <InstallationSearch
                        installations={salidomoInstallations}
                      />
                    </Box>
                  </Grid>
                }
              />

              <Route path={routes.tree + '*'} element={<TreeView />} />

              <Route
                path={'*'}
                element={
                  <Navigate
                    to={routes.salidomo_installations + routes.list}
                  ></Navigate>
                }
              ></Route>
            </Routes>
          </Grid>
        </Card>
      </Container>
    </>
  ) : salidomoInstallations.length === 1 ? (
    <>
      {' '}
      <Container maxWidth="xl" sx={{ marginTop: '20px' }} className="mainframe">
        <TabsContainerWrapper>
          <Tabs
            onChange={handleTabsChange}
            value={currentTab}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            textColor="primary"
            indicatorColor="primary"
          >
            {singleInstallationTabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                component={Link}
                label={tab.label}
                to={routes[tab.value]}
                id={`tour-tab-${tab.value}`}
              />
            ))}
          </Tabs>
        </TabsContainerWrapper>
        <Card variant="outlined">
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="stretch"
            spacing={0}
          >
            <Routes>
              <Route
                path={'*'}
                element={
                  <Grid item xs={12}>
                    <Box p={4}>
                      <SalidomoInstallation
                        current_installation={salidomoInstallations[0]}
                        type="installation"
                      ></SalidomoInstallation>
                    </Box>
                  </Grid>
                }
              />
            </Routes>
          </Grid>
        </Card>
      </Container>
    </>
  ) : null;
}

export default SalidomoInstallationTabs;
